import React, { useEffect, useState } from "react";
import { getOrders, performOrderAction, setOrderIsPaid } from "@utils/api-communication/RestApiCli";
import {
  Select,
  Table,
  Loader,
  Text,
  Title,
  Badge,
  Container,
  Button,
  Modal,
  Chip,
} from "@mantine/core";
import { useDispatch, useSelector } from "react-redux";
import { selectUserRole } from "@utils/redux-utils/selectors/userSelectors";
import { Order, OrderStatusValues } from "types/accountManagementTypes";
import { openConfirmation } from "@actions/actions";
import { useNavigate } from "react-router-dom";
import LicenseAssignmentRequestsButton from "@components/management/accountManagement/LicenseAssignmentRequestsButton";

const ORDER_STATUS_OPTIONS = [
  { value: "", label: "All" },
  ...OrderStatusValues.map((status) => ({
    value: status,
    label: status.charAt(0).toUpperCase() + status.slice(1),
  })),
];

const OrderList: React.FC = () => {
  const [statusFilter, setStatusFilter] = useState<string | null>("");
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [isPaid, setIsPaid] = useState<number>(0);
  const role = useSelector(selectUserRole);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const load = (status?: string | null) => {
    setLoading(true);
    setError(null);
    const queryParams: any = {};
    if (status && status !== "") {
      queryParams.status = status;
    }
    if (selectedOrderId) {
      queryParams.orderId = selectedOrderId;
    }
    getOrders(
      queryParams,
      (resp: any) => {
        if (selectedOrderId) {
          setSelectedOrder(resp?.data);
          setLoading(false);
        } else {
          setOrders(resp?.data || []);
          setLoading(false);
        }
      },
      (err: any) => {
        console.error(err);
        setError(err?.message || "Failed to fetch orders");
        setLoading(false);
      },
    );
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    load(statusFilter && statusFilter !== "" ? statusFilter : "");
  }, [statusFilter]);

  // trigger fetch when a specific order is selected
  useEffect(() => {
    if (selectedOrderId) {
      load();
    }
  }, [selectedOrderId]);

  // action state
  const [actionLoading, setActionLoading] = useState<Record<string, boolean>>({});
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [updatingPaid, setUpdatingPaid] = useState(false); // added

  const markActionLoading = (orderId: string) =>
    setActionLoading((s) => ({ ...s, [orderId]: true }));
  const clearActionLoading = (orderId: string) =>
    setActionLoading((s) => ({ ...s, [orderId]: false }));

  const handlePerformAction = (_order_id: string, action: string) => {
    markActionLoading(_order_id);
    performOrderAction(
      _order_id,
      action,
      () => {
        clearActionLoading(_order_id);
        load(statusFilter && statusFilter !== "" ? statusFilter : "");
      },
      (err) => {
        console.error(err);
        clearActionLoading(_order_id);
        setError(err?.message || `Failed to ${action}`);
      },
    );
  };

  const openUpdateModal = (order: Order) => {
    setSelectedOrder(null); // clear any previous selection data
    setSelectedOrderId(order._id); // this will trigger load via effect
    setUpdateModalOpen(true);
  };

  const closeUpdateModal = () => {
    setUpdateModalOpen(false);
    setSelectedOrderId(null);
    setSelectedOrder(null);
  };

  // helpers for modal rendering
  const formatDate = (d?: string | null) => (d ? new Date(d).toLocaleString() : "-");
  const pickKey = (obj: any, keys: string[]) => keys.find((k) => obj && obj[k] != null);
  const getItemName = (it: any) => (it && it[pickKey(it, ["licenseType"] as const) as any]) ?? "-";
  const getItemQty = (it: any) => (it && it[pickKey(it, ["quantity"] as const) as any]) ?? "-";

  // sync isPaid toggle with loaded order
  useEffect(() => {
    if (selectedOrder) {
      setIsPaid(selectedOrder.isPaid ? 1 : 0);
    }
  }, [selectedOrder]);

  const handleUpdatePaidStatus = () => {
    if (!selectedOrder) return;
    setUpdatingPaid(true);
    setOrderIsPaid(
      selectedOrder._id,
      isPaid === 1,
      () => {
        // refresh table and local state
        setSelectedOrder((prev) => (prev ? ({ ...prev, isPaid: isPaid === 1 } as Order) : prev));
        load(statusFilter && statusFilter !== "" ? statusFilter : "");
        setUpdatingPaid(false);
      },
      (err: any) => {
        console.error(err);
        setError(err?.message || "Failed to update payment status");
        setUpdatingPaid(false);
      },
    );
  };

  return (
    <Container size="lg" mt="md">
      <div className="order-list">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <Title order={3}>Orders</Title>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <Select
              data={ORDER_STATUS_OPTIONS}
              value={statusFilter ?? ""}
              onChange={(val) => setStatusFilter(val)}
              placeholder="Filter by status"
              style={{ minWidth: 220 }}
              clearable
            />
            <LicenseAssignmentRequestsButton />
            <Button onClick={() => navigate("/create-order")}>Create Order</Button>
          </div>
        </div>

        {loading && !updateModalOpen && (
          <div style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 8 }}>
            <Loader />
            <Text>Loading...</Text>
          </div>
        )}

        {error && !updateModalOpen && <Text style={{ color: "red", marginTop: 16 }}>{error}</Text>}

        {!loading && !error && (
          <Table mt="md" horizontalSpacing="md" verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Order #</Table.Th>
                <Table.Th>Year</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Paid</Table.Th>
                <Table.Th>Items</Table.Th>
                <Table.Th>Distributor</Table.Th>
                <Table.Th>Created</Table.Th>
                <Table.Th>Updated</Table.Th>
                <Table.Th>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {orders.length === 0 && (
                <tr>
                  <td colSpan={8} style={{ padding: 12 }}>
                    <Text>No orders found.</Text>
                  </td>
                </tr>
              )}
              {orders.map((o) => (
                <Table.Tr key={o._id}>
                  <Table.Td>{o.orderNumber}</Table.Td>
                  <Table.Td>{o.orderYear}</Table.Td>
                  <Table.Td>
                    <Badge
                      color={
                        o.status === "approved" ? "green" : o.status === "rejected" ? "red" : "gray"
                      }
                    >
                      {o.status}
                    </Badge>
                  </Table.Td>
                  <Table.Td>{o.isPaid ? "Yes" : "No"}</Table.Td>
                  <Table.Td>{o.orderItems?.length ?? 0}</Table.Td>
                  <Table.Td>{o.distId}</Table.Td>
                  <Table.Td>{o.createdAt ? new Date(o.createdAt).toLocaleString() : ""}</Table.Td>
                  <Table.Td>{o.updatedAt ? new Date(o.updatedAt).toLocaleString() : ""}</Table.Td>
                  <Table.Td>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      {role === "admin" && (
                        <>
                          <Button
                            size="xs"
                            color="green"
                            disabled={!!actionLoading[o._id]}
                            onClick={() =>
                              dispatch(
                                openConfirmation(
                                  "Sure?",
                                  `Are you sure you want to approve order #${o.orderNumber}?`,
                                  () => handlePerformAction(o._id, "approved"),
                                  false,
                                  "Close",
                                  "Confirm",
                                ),
                              )
                            }
                          >
                            {actionLoading[o._id] ? "..." : "Approve"}
                          </Button>
                          <Button
                            size="xs"
                            color="red"
                            disabled={!!actionLoading[o._id]}
                            onClick={() => handlePerformAction(o._id, "rejected")}
                          >
                            {actionLoading[o._id] ? "..." : "Reject"}
                          </Button>
                          <Button size="xs" onClick={() => openUpdateModal(o)}>
                            Update
                          </Button>
                        </>
                      )}

                      {role === "distributor" && (
                        <>
                          <Button
                            size="xs"
                            color="red"
                            disabled={!!actionLoading[o._id]}
                            onClick={() => handlePerformAction(o._id, "cancelled")}
                          >
                            {actionLoading[o._id] ? "..." : "Cancel"}
                          </Button>
                        </>
                      )}
                    </div>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}

        <Modal opened={updateModalOpen} onClose={closeUpdateModal} title="Order details">
          {/* When viewing a specific order, show loading/error or the formatted object */}
          {loading && (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Loader />
              <Text>Loading order...</Text>
            </div>
          )}
          {error && <Text style={{ color: "red" }}>{error}</Text>}

          {!loading && !error && selectedOrder && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <Table>
                <Table.Tbody>
                  <Table.Tr>
                    <Table.Td style={{ fontWeight: 600, width: 160 }}>Order #</Table.Td>
                    <Table.Td>{selectedOrder.orderNumber ?? "-"}</Table.Td>
                  </Table.Tr>
                  <Table.Tr>
                    <Table.Td style={{ fontWeight: 600 }}>Year</Table.Td>
                    <Table.Td>{selectedOrder.orderYear ?? "-"}</Table.Td>
                  </Table.Tr>
                  <Table.Tr>
                    <Table.Td style={{ fontWeight: 600 }}>Status</Table.Td>
                    <Table.Td>
                      <Badge
                        color={
                          selectedOrder.status === "approved"
                            ? "green"
                            : selectedOrder.status === "rejected"
                              ? "red"
                              : "gray"
                        }
                      >
                        {selectedOrder.status}
                      </Badge>
                    </Table.Td>
                  </Table.Tr>
                  <Table.Tr>
                    <Table.Td style={{ fontWeight: 600 }}>Paid</Table.Td>
                    <Table.Td>
                      <Chip checked={!!isPaid} onChange={(checked) => setIsPaid(checked ? 1 : 0)}>
                        {isPaid === 1 ? "Yes" : "No"}
                      </Chip>
                    </Table.Td>
                  </Table.Tr>
                  <Table.Tr>
                    <Table.Td style={{ fontWeight: 600 }}>Items</Table.Td>
                    <Table.Td>{selectedOrder.orderItems?.length ?? 0}</Table.Td>
                  </Table.Tr>
                  <Table.Tr>
                    <Table.Td style={{ fontWeight: 600 }}>Distributor</Table.Td>
                    <Table.Td>{selectedOrder.distId ?? "-"}</Table.Td>
                  </Table.Tr>
                  <Table.Tr>
                    <Table.Td style={{ fontWeight: 600 }}>Created</Table.Td>
                    <Table.Td>{formatDate(selectedOrder.createdAt as any)}</Table.Td>
                  </Table.Tr>
                  <Table.Tr>
                    <Table.Td style={{ fontWeight: 600 }}>Updated</Table.Td>
                    <Table.Td>{formatDate(selectedOrder.updatedAt as any)}</Table.Td>
                  </Table.Tr>
                </Table.Tbody>
              </Table>

              {!!selectedOrder.orderItems?.length && (
                <>
                  <Title order={6} style={{ marginTop: 4 }}>
                    Items ({selectedOrder.orderItems.length})
                  </Title>
                  <Table>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th style={{ width: 48 }}>#</Table.Th>
                        <Table.Th>Type</Table.Th>
                        <Table.Th style={{ width: 120 }}>Quantity</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {selectedOrder.orderItems.map((it: any, idx: number) => (
                        <Table.Tr key={idx}>
                          <Table.Td>{idx + 1}</Table.Td>
                          <Table.Td>{getItemName(it)}</Table.Td>
                          <Table.Td>{getItemQty(it)}</Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </>
              )}
            </div>
          )}

          {!loading && !error && !selectedOrder && (
            <Text size="sm">No data available for this order.</Text>
          )}
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12, gap: 8 }}>
            <Button variant="default" onClick={closeUpdateModal} disabled={updatingPaid}>
              Close
            </Button>
            <Button
              onClick={handleUpdatePaidStatus}
              loading={updatingPaid}
              disabled={!selectedOrder}
            >
              Update
            </Button>
          </div>
        </Modal>
      </div>
    </Container>
  );
};

export default OrderList;

// Render create order drawer at end so it overlays correctly
